import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCustomerValidation } from '@/components/CustomerValidationProvider';
import { AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DynamicFieldValidatorProps {
  data: Record<string, any>;
  onFieldChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
  doubleClickToPasteHook?: {
    getFieldProps: (fieldName: string) => any;
    focusedField: string | null;
    selectedText: string;
    showFeedback: boolean;
  };
}

export const DynamicFieldValidator = ({ 
  data = {}, 
  onFieldChange, 
  disabled = false,
  doubleClickToPasteHook
}: DynamicFieldValidatorProps) => {
  const {
    configsByGroup = {},
    validationState = {},
    updateFieldValue,
    getFieldConfig,
    validateAllFields
  } = useCustomerValidation() || {};

  // Initialize validation when component mounts or data changes
  useEffect(() => {
    if (data && Object.keys(data).length > 0 && validateAllFields) {
      validateAllFields(data);
    }
  }, [data, validateAllFields]);

  const handleFieldChange = (fieldName: string, value: any) => {
    if (onFieldChange) {
      onFieldChange(fieldName, value);
    }
    if (updateFieldValue) {
      updateFieldValue(fieldName, value);
    }
  };

  const renderField = (config: any) => {
    if (!config) return null;
    
    const value = data?.[config.field_name] || config.default_value || '';
    const validation = validationState?.[config.field_name];
    const hasError = validation && !validation.valid;
    const isFocused = doubleClickToPasteHook?.focusedField === config.field_name;

    const commonProps = {
      id: config.field_name,
      value,
      onChange: (e: any) => handleFieldChange(config.field_name, e.target?.value || e),
      disabled,
      className: `
        ${hasError ? 'border-destructive' : ''} 
        ${isFocused ? 'ring-2 ring-primary/50' : ''}
      `.trim(),
      ...doubleClickToPasteHook?.getFieldProps(config.field_name)
    };

    let field;
    switch (config.field_type) {
      case 'textarea':
        field = <Textarea {...commonProps} rows={3} />;
        break;
      case 'select':
        const options = config.validation_rules?.options || [];
        const validOptions = options.filter((option: any) => option?.value && option.value.trim() !== '');
        field = (
          <Select value={value} onValueChange={(val) => handleFieldChange(config.field_name, val)} disabled={disabled}>
            <SelectTrigger className={hasError ? 'border-destructive' : ''}>
              <SelectValue placeholder={`Select ${config.display_name || 'option'}`} />
            </SelectTrigger>
            <SelectContent>
              {validOptions.map((option: any, index: number) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label || option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
        break;
      case 'number':
        field = <Input {...commonProps} type="number" step="0.01" />;
        break;
      case 'email':
        field = <Input {...commonProps} type="email" />;
        break;
      case 'date':
        field = <Input {...commonProps} type="date" />;
        break;
      default:
        field = <Input {...commonProps} type="text" />;
    }

    return (
      <div key={config?.id || config?.field_name} className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor={config?.field_name} className="flex items-center gap-2">
            {config?.display_name || config?.field_name || 'Unknown Field'}
            {config?.is_mandatory && (
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            )}
            {config?.priority === 'customer' && (
              <Badge variant="secondary" className="text-xs">
                Customer-specific
              </Badge>
            )}
          </Label>
          
          {config?.help_text && (
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{config.help_text}</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {validation && (
            <div className="ml-auto">
              {validation.valid ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-destructive" />
              )}
            </div>
          )}
        </div>
        
        {field}
        
        {validation && !validation.valid && validation.errors && (
          <div className="space-y-1">
            {validation.errors.map((error, index) => (
              <Alert key={index} variant="destructive" className="py-2">
                <AlertCircle className="h-3 w-3" />
                <AlertDescription className="text-xs">{error || 'Validation error'}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (Object.keys(configsByGroup).length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Validation Rules</h3>
          <p className="text-muted-foreground">
            No field validation rules are configured for this customer.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(configsByGroup || {}).map(([group, configs]) => {
        if (!configs || !Array.isArray(configs)) return null;
        
        return (
          <Card key={group}>
            <CardHeader>
              <CardTitle className="text-base capitalize flex items-center gap-2">
                {group?.replace('_', ' ') || 'Unknown Group'}
                <Badge variant="outline" className="text-xs">
                  {configs.filter(c => c?.is_mandatory).length} required
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              {configs
                .filter(config => config != null)
                .sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0))
                .map(renderField)
                .filter(Boolean)}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};